import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Heading,
  Text,
  Input,
  Textarea,
  VStack,
  HStack,
  Grid,
  GridItem,
  Flex,
  NativeSelect,
  Spinner
} from "@chakra-ui/react";

import OrderService from "./OrderService";
import CollectionConfigurationService from "./CollectionConfigurationService";
import ChainBuilder from "./ChainBuilder";
import DataTailorService from "./DataTailorService";
import { DataTailorDisclaimer } from "./Disclaimers";

const makeId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : String(Math.random()).slice(2);

export default function CreateOrderView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getInitialValue = (key, defaultValue) => {
    if (searchParams.has(key)) return searchParams.get(key);
    return defaultValue;
  };

  // Check if fields were provided via URL to lock them
  const isCollectionIdReadOnly = searchParams.has("collectionId");
  const isProductIdsReadOnly = searchParams.has("productIds");
  const isProductTypeReadOnly = searchParams.has("productType");

  // View & Submission State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Core Form State
  const [collectionId, setCollectionId] = useState(getInitialValue("collectionId", ""));
  // One or more Data Tailor chain steps; each step's output feeds the next.
  const [steps, setSteps] = useState([{ id: makeId(), config: {} }]);
  const [productIds, setProductIds] = useState(getInitialValue("productIds", ""));
  const [productType, setProductType] = useState(getInitialValue("productType", ""));
  const [ltaStartDate, setLtaStartDate] = useState("");
  const [ltaEndDate, setLtaEndDate] = useState("");
  const [notificationEmail, setNotificationEmail] = useState(getInitialValue("notificationEmail", ""));

  // Async Validation State
  const [activeRules, setActiveRules] = useState(null);
  const [isRulesLoading, setIsRulesLoading] = useState(false);

  // Async Effect to fetch collection rules
  useEffect(() => {
    if (!collectionId || collectionId.trim() === "") {
      setActiveRules(null);
      return;
    }

    let ignore = false; 

    const fetchRules = async () => {
      setIsRulesLoading(true);
      try {
        const rules = await CollectionConfigurationService.retrieveCollectionRules(collectionId);
        if (!ignore) {
          setActiveRules(rules);
          // Auto-clear the productType if it doesn't exist in the new rules
          if (rules && !rules.validProductTypes.includes(productType)) {
             setProductType("");
          }
        }
      } catch (error) {
        console.error("Failed to load collection rules:", error);
        if (!ignore) {
          setActiveRules(null);
          setProductType("");
        }
      } finally {
        if (!ignore) {
          setIsRulesLoading(false);
        }
      }
    };

    // Debounce to prevent floding
    const debounceTimer = setTimeout(() => {
      fetchRules();
    }, 300); 

    return () => {
      ignore = true; 
      clearTimeout(debounceTimer);
    };
  }, [collectionId, productType]); 

  // Derived Processing Flags
  const validSteps = steps.map((s) => s.config).filter((c) => c && c.product && c.format);
  const requiresTransformation = validSteps.length > 0;
  const requiresOnlineProducts = productIds.trim().length > 0;

  // Count of online product IDs, used as nr_input_products for cost estimation.
  const inputProductCount = productIds
    .split(/\n+/).map((s) => s.trim()).filter(Boolean).length || 1;

  const updateStep = (id, config) =>
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, config } : s)));
  const addStep = () => setSteps((prev) => [...prev, { id: makeId(), config: {} }]);
  const removeStep = (id) =>
    setSteps((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : prev));
  const requiresLta =
    productType.trim().length > 0 ||
    ltaStartDate.trim().length > 0 ||
    ltaEndDate.trim().length > 0;

  // Submit Button State Assessment
  const isOnlyLta = requiresLta && !requiresOnlineProducts && !requiresTransformation;
  const isOnlineAndTrans = !requiresLta && requiresOnlineProducts && requiresTransformation;
  const isLtaAndTrans = requiresLta && !requiresOnlineProducts && requiresTransformation;
  const isAllThree = requiresLta && requiresOnlineProducts && requiresTransformation;

  const isJobCombinationValid = isOnlyLta || isOnlineAndTrans || isLtaAndTrans || isAllThree;

  const availableProductTypes = activeRules ? activeRules.validProductTypes : [];

  let orderType;
  if (isOnlyLta) 
    orderType = 'LTA';
  if (isOnlineAndTrans) 
    orderType = 'DT';
  if (isLtaAndTrans) 
    orderType = 'LTA_DT';
  if (isAllThree) 
    orderType = 'HYBRID_DT';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccess(false);
    setSubmitError(null);

    const productIdsArray = productIds
      .split(/\n+/)
      .map(id => id.trim())
      .filter(id => id.length > 0);

    // Compatibility guard: a chain product class registered for a different
    // collection (pn_id from the DT catalog) deterministically fails epct's
    // input check inside the pod - reject it here instead. Unknown pn_id never
    // blocks, and neither does a catalog outage.
    if (requiresTransformation && requiresOnlineProducts && collectionId.trim()) {
      try {
        const catalog = await DataTailorService.getCatalog();
        const dtProducts = catalog?.resources?.products || [];
        for (const cfg of validSteps) {
          const entry = dtProducts.find((p) => p.id === cfg.product);
          if (entry && DataTailorService.isProductCompatible(entry, collectionId.trim()) === false) {
            const suggestions = DataTailorService
              .productsForCollection(dtProducts, collectionId.trim())
              .map((p) => p.id);
            setSubmitError(
              `Chain product '${cfg.product}' is not registered for collection '${collectionId.trim()}'` +
                (suggestions.length ? ` - compatible products: ${suggestions.join(', ')}` : '') +
                '. The job would fail the Data Tailor input check.'
            );
            setIsSubmitting(false);
            return;
          }
        }
      } catch (e) {
        // Catalog unavailable: never block ordering on the compatibility check.
      }
    }

    // Pre-check: reject products missing from the EDL catalogue before placing
    // the order - otherwise the job dispatches and fails at resolution with the
    // same 404, hours later. Unverifiable checks (catalogue down) never block.
    if (requiresOnlineProducts) {
      const checks = await Promise.all(
        productIdsArray.map(async (id) => ({
          id,
          ...(await DataTailorService.checkProductAvailability(collectionId, id)),
        }))
      );
      const unavailable = checks.filter((c) => c.available === false);
      if (unavailable.length > 0) {
        setSubmitError(
          'Cannot place the order: ' +
            unavailable.map((c) => `'${c.id}' - ${c.reason}`).join('; ')
        );
        setIsSubmitting(false);
        return;
      }
    }

    const formattedStartDate = ltaStartDate ? new Date(ltaStartDate).toISOString() : "";
    const formattedEndDate = ltaEndDate ? new Date(ltaEndDate).toISOString() : "";

    const inputs = {};

    if (requiresLta) {
      inputs.lta = {
        requests: [{
          collectionId: collectionId,
          productType: productType,
          timeInterval: {
            startDate: formattedStartDate,
            endDate:   formattedEndDate
          }
        }]
      };
    }

    if (requiresTransformation) {
      // Single step -> YAML (current OJM/DTS path). Multiple steps -> a JSON
      // {steps:[...]} that OJM detects and forwards as a chained workflow.
      const transformationValue = validSteps.length > 1
        ? JSON.stringify({ steps: validSteps })
        : DataTailorService.chainConfigToYaml(validSteps[0]);
      inputs.processing = {
        transformation: { value: transformationValue, steps: validSteps },
        ...(requiresOnlineProducts && {
          products: [{ collectionId: collectionId, productIds: productIdsArray }]
        })
      };
    }

    const payload = {
      type: orderType,
      inputs,
      notifications: { value: notificationEmail }
    };

    console.log("Submitting Payload:", JSON.stringify(payload, null, 2));

    try {
      await OrderService.createOrder(payload);
      setSuccess(true);
      setTimeout(() => navigate("/orders"), 3000);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitError("Failed to submit the order. Please check your network connection or try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="sm" position="relative">

      <Heading size="lg" color="gray.800" mb={2}>Create New Order</Heading>
      <Text color="gray.600" mb={8}>
        Configure your dataset parameters and submit a new processing job.
      </Text>

      <form onSubmit={handleSubmit}>
        <VStack align="stretch" gap={8}>

          <Box p={5} border="1px solid" borderColor="gray.200" borderRadius="md">
            <Heading size="sm" mb={4} color="gray.700">1. Target Collection</Heading>
            <Box maxW="500px">
              <Text mb={1} fontSize="sm" fontWeight="600">Collection ID</Text>
              <Input
                value={collectionId}
                onChange={(e) => setCollectionId(e.target.value)}
                required
                placeholder="e.g., EO:EUM:DAT:METOP:SOMO25"
                readOnly={isCollectionIdReadOnly}
                bg={isCollectionIdReadOnly ? "gray.50" : "white"}
                color={isCollectionIdReadOnly ? "gray.600" : "inherit"}
                _readOnly={{ borderColor: "gray.200", cursor: "not-allowed" }}
              />
            </Box>
          </Box>

          <Box p={5} border="1px solid" borderColor="gray.200" borderRadius="md">
            <Heading size="sm" mb={2} color="gray.700">2. Transformation (Data Tailor chain)</Heading>
            <DataTailorDisclaimer />
            <Text fontSize="sm" color="gray.500" mb={4}>
              Build one or more Data Tailor steps from the catalog. With multiple steps, each step's
              output feeds the next (chained workflow). Leave product/format empty if no transformation is required.
            </Text>
            <VStack align="stretch" gap={6}>
              {steps.map((step, idx) => (
                <Box key={step.id} p={4} border="1px solid" borderColor="gray.100" borderRadius="md" bg="gray.50">
                  <HStack justify="space-between" mb={3}>
                    <Heading size="xs" color="gray.600">Step {idx + 1}</Heading>
                    {steps.length > 1 && (
                      <Button size="xs" variant="ghost" onClick={() => removeStep(step.id)}>Remove</Button>
                    )}
                  </HStack>
                  <ChainBuilder
                    value={step.config}
                    onChange={(cfg) => updateStep(step.id, cfg)}
                    inputProductCount={inputProductCount}
                    collectionId={requiresOnlineProducts ? collectionId.trim() : ""}
                  />
                </Box>
              ))}
            </VStack>
            <Button mt={4} size="sm" variant="outline" onClick={addStep}>+ Add step</Button>
          </Box>

          <Box p={5} border="1px solid" borderColor="gray.200" borderRadius="md">
            <Heading size="sm" mb={4} color="gray.700">3. Online Data</Heading>
            <Text fontSize="sm" color="gray.500" mb={4}>If product IDs are provided, it is required to also provide the transformation parameter.</Text>
            <Text mb={1} fontSize="sm" fontWeight="600">Product IDs (separated by line)</Text>
            <Textarea
              value={productIds}
              onChange={(e) => setProductIds(e.target.value)}
              fontFamily="mono" fontSize="sm" rows={4}
              placeholder="Leave blank if no online products are required"
              readOnly={isProductIdsReadOnly}
              bg={isProductIdsReadOnly ? "gray.50" : "white"}
              color={isProductIdsReadOnly ? "gray.600" : "inherit"}
              _readOnly={{ borderColor: "gray.200", cursor: "not-allowed" }}
            />
          </Box>

          <Box p={5} border="1px solid" borderColor="gray.200" borderRadius="md">
            <HStack justify="space-between" mb={4}>
              <Heading size="sm" color="gray.700">4. Long Term Archive (LTA) Request</Heading>
              {isRulesLoading && <Spinner size="sm" color="blue.500" />}
            </HStack>
            <Text fontSize="sm" color="gray.500" mb={4}>Leave these fields blank if LTA is not required.</Text>

            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
              <GridItem>
                <Text mb={1} fontSize="sm" fontWeight="600">Product Type</Text>
                <NativeSelect.Root disabled={isProductTypeReadOnly || !collectionId || availableProductTypes.length === 0 || isRulesLoading}>
                  <NativeSelect.Field
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                    bg={isProductTypeReadOnly ? "gray.50" : "white"}
                    color={isProductTypeReadOnly ? "gray.600" : "inherit"}
                  >
                    {isRulesLoading ? (
                      <option value="">Loading...</option>
                    ) : (
                      <option value="">Select a type...</option>
                    )}
                    {availableProductTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </GridItem>

              <GridItem>
                <Text mb={1} fontSize="sm" fontWeight="600">Start Date</Text>
                <Input
                  type="datetime-local"
                  value={ltaStartDate}
                  onChange={(e) => setLtaStartDate(e.target.value)}
                  min={activeRules?.minDate}
                  max={activeRules?.maxDate}
                  disabled={isRulesLoading}
                />
              </GridItem>

              <GridItem>
                <Text mb={1} fontSize="sm" fontWeight="600">End Date</Text>
                <Input
                  type="datetime-local"
                  value={ltaEndDate}
                  onChange={(e) => setLtaEndDate(e.target.value)}
                  min={activeRules?.minDate}
                  max={activeRules?.maxDate}
                  disabled={isRulesLoading}
                />
              </GridItem>
            </Grid>
          </Box>

          <Box>
            <Heading size="sm" mb={4} color="gray.700">5. Notifications</Heading>
            <Text mb={1} fontSize="sm" fontWeight="600">Notification Email</Text>
            <Input
              type="email"
              value={notificationEmail}
              onChange={(e) => setNotificationEmail(e.target.value)}
              maxW="400px" required placeholder="e.g., user@example.com"
            />
          </Box>

          <Box p={5} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
            <Heading size="sm" mb={4} color="gray.700">Job meta information</Heading>
            <HStack gap={6} flexWrap="wrap">
              <Flex align="center" gap={2}>
                <input type="checkbox" checked={requiresLta} readOnly style={{ accentColor: '#3182ce', cursor: 'default' }} />
                <Text fontWeight="medium" color="gray.700">Requires LTA</Text>
              </Flex>
              <Flex align="center" gap={2}>
                <input type="checkbox" checked={requiresTransformation} readOnly style={{ accentColor: '#3182ce', cursor: 'default' }} />
                <Text fontWeight="medium" color="gray.700">Requires Transformation</Text>
              </Flex>
              <Flex align="center" gap={2}>
                <input type="checkbox" checked={requiresOnlineProducts} readOnly style={{ accentColor: '#3182ce', cursor: 'default' }} />
                <Text fontWeight="medium" color="gray.700">Requires Online Products</Text>
              </Flex>
            </HStack>
          </Box>


          <Box pt={4}>
            {!isJobCombinationValid && (
              <Text color="red.500" fontSize="sm" mb={3} fontWeight="medium">
                Invalid configuration. You must provide a valid combination of LTA data, Online Products, and/or Transformations.
              </Text>
            )}

            {submitError && (
              <Text color="red.500" fontSize="sm" mb={3} fontWeight="medium">
                {submitError}
              </Text>
            )}
            
            <HStack>
              <Button
                type="submit" bg="blue.600" color="white" _hover={{ bg: "blue.700" }} size="lg"
                disabled={isSubmitting || success || !isJobCombinationValid}
              >
                {isSubmitting ? "Submitting..." : "Submit Order"}
              </Button>

              {success && (
                <Text color="green.600" fontWeight="medium" ml={4}>
                  ✓ Order submitted successfully! Redirecting to orders in 3 seconds...
                </Text>
              )}
            </HStack>
          </Box>

        </VStack>
      </form>
    </Box>
  );
}